import { JobDescriptionEntity, Requirements } from "../Entities/JobDescription";
import { JobPostsFilters, UpdateJobDescriptionPayload } from "../Interface";
import { EventDispatcher } from "../Utils/event-dispatcher";
import apiClient from "./api.client.service";
import { parseJobPostDates } from "../Utils/generic-functions";
import googleAnalyticService from "./google.analytic.service";
import { InterviewRound } from "../Dto/GenerateJobDescription.dto";

class JobDescriptionService extends EventDispatcher {
  private jobDescriptions: JobDescriptionEntity[] = [];
  private APIBaseUrl: string;
  private limit = 10;

  constructor() {
    super();
    this.APIBaseUrl = process.env.REACT_APP_API_BASE_URL ?? "";
  }

  async getStatisticsCounts() {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/job-description/stats/overall`
      );
      const data = response.data;
      return data;
    } catch (error) {
      console.error(
        "🚀 ~ JobDescriptionService ~ getStatisticsCounts ~ error:",
        error
      );
    }
  }

  async getAllJobDescriptions(
    page: number,
    limit: number = 10,
    sortby?: string,
    order?: string,
    startDate?: string,
    endDate?: string,
    search?: string
  ) {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append("page", page.toString());
      queryParams.append("limit", limit.toString());

      // Add optional parameters if provided
      if (sortby) queryParams.append("sortby", sortby);
      if (order) queryParams.append("order", order);
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);
      if (search) queryParams.append("search", search);

      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/job-description?${queryParams.toString()}`
      );

      const data = response?.data?.data;
      this.jobDescriptions = data;
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ JobDescriptionService ~ getAllJobDescriptions ~ error:",
        error
      );
    }
  }

  async getJobDescriptionById(
    id: string
  ): Promise<JobDescriptionEntity | null> {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/job-description/${id}`
      );
      if (response.data === null) {
        throw new Error("Job description not found");
      }

      const parsedData = parseJobPostDates(response.data);

      return parsedData;
    } catch (error) {
      console.error(
        "🚀 ~ JobDescriptionService ~ getJobDescriptionById ~ error:",
        error
      );
      return null;
    }
  }

  async addJobDescription(
    jobDescription: Partial<JobDescriptionEntity>,
    orgName: string,
    isActive: boolean = true
  ) {
    try {
      const payload: any = {
        title: jobDescription.title,
        description: jobDescription.description,
        position: jobDescription.position,
        isActive,
        numberOfOpenings: jobDescription.numberOfOpenings,
        requirements: jobDescription.requirements || [],
        additionalRequirements: jobDescription.additionalRequirements || [],
        interviewers: jobDescription.interviewers || [],
        ctc: jobDescription?.ctc,
        noticePeriod: jobDescription?.noticePeriod || 0,
        interviewRounds: jobDescription?.interviewRounds || [],
        endDate: jobDescription?.endDate,
        onlineInterview: jobDescription?.onlineInterview ?? false,
        interviewGuests: jobDescription?.interviewGuests || [],
        interviewRoundExpiry: jobDescription?.interviewRoundExpiry || 5,
        isProfileMatchRequired: jobDescription?.isProfileMatchRequired ?? true,

        // ATS integration fields (for syncing imported job posts)
        appJobId: jobDescription?.appJobId,
        appType: jobDescription?.appType,

        // Skills extracted from AI generation
        skills: (jobDescription as any)?.skills || [],

        // Candidate notification preferences
        notifications: jobDescription?.notifications ?? { email: true, whatsapp: true },
      };

      // Add currency as separate field if provided
      if ((jobDescription as any).currency) {
        payload.currency = (jobDescription as any).currency;
      } else if (
        jobDescription?.ctc &&
        typeof jobDescription.ctc === "number"
      ) {
        // Default to INR if CTC is provided but currency is missing
        payload.currency = "INR";
      }

      console.log(
        "[JobDescriptionService] addJobDescription payload:",
        JSON.stringify(payload, null, 2)
      );

      const response = await apiClient.post(
        `${this.APIBaseUrl}/admin/job-description`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const data = response.data;
      this.dispatch("JOB_POST_ADDED");
      this.jobDescriptions.push(data);
      googleAnalyticService.handleSubmissionEvents({
        category: "Job Post",
        action: "job_post_created",
        job_post_name: data.title,
        organization_name: orgName,
        label: `New Job post titled ${data.title} has been created on organization ${orgName}`,
      });
      return data;
    } catch (error) {
      console.error(
        "🚀 ~ JobDescriptionService ~ addJobDescription ~ error:",
        error
      );
      throw error; // Re-throw error so calling code can handle it
    }
  }

  async updateJobDescriptionStatus(jobId: string, newStatus: string) {
    try {
      const response = await apiClient.patch(
        `${this.APIBaseUrl}/admin/job-description/${jobId}/${newStatus}`
      );
      this.dispatch("JOB_POST_STATUS_CHANGED", { jobId, status: newStatus });
      return response.data;
    } catch (error) {
      console.error("🚀 ~ JobDescriptionService ~  error:", error);
    }
  }

  async getAllJobDescriptionWithStatistics(page: number) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/jobDescriptions/statistics?page=${page}`
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ JobDescriptionService ~ getAllJobDescriptionWithStatistics ~ error:",
        error
      );
    }
  }

  async getCompleteJobDataByJobDescriptionId(data: any) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/job-application`,
        {
          params: {
            page: data.page,
            jobDescriptionId: data.jobDescriptionId,
            status: data.status,
            userName: data.userName,
            limit: data.limit,
            interviewStatus: data.interviewStatus,
            sort: data.sort,
            questionnaireStatusFilter: data.questionnaireStatusFilter,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ JobDescriptionService ~ getCompleteJobDataByJobDescriptionId ~ error:",
        error
      );
    }
  }

  async updateJobDescription(jobDescription: JobDescriptionEntity, id: string) {
    try {
      const payload: any = {
        ...jobDescription,
        // numberOfPendingInterviews is managed by backend - no need to send it in updates
        // updatedAt is managed by backend - no need to send it
      };
      // Remove fields that backend manages internally
      delete payload.numberOfPendingInterviews;
      delete payload.updatedAt;
      delete payload.createdAt;

      // Handle currency field - ensure it's sent as separate field
      if ((jobDescription as any).currency) {
        payload.currency = (jobDescription as any).currency;
      } else if (jobDescription.ctc && typeof jobDescription.ctc === "number") {
        // Default to INR if CTC is provided but currency is missing
        payload.currency = "INR";
      }

      const response = await apiClient.post(
        `${this.APIBaseUrl}/admin/job-description/update/${id}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ JobDescriptionService ~ updateJobDescription ~ error:",
        error
      );
      throw error; // Re-throw error so calling code can handle it
    }
  }

  async updateJobDescriptionV2(
    jobDescription: UpdateJobDescriptionPayload,
    id: string
  ) {
    try {
      const payload: any = {
        ...jobDescription,
        // numberOfPendingInterviews is managed by backend - no need to send it in updates
        // updatedAt is managed by backend - no need to send it
      };
      // Remove fields that backend manages internally
      delete payload.numberOfPendingInterviews;
      delete payload.updatedAt;
      delete payload.createdAt;

      // Handle currency field - ensure it's sent as separate field
      if (jobDescription.currency) {
        payload.currency = jobDescription.currency;
      } else if (jobDescription.ctc && typeof jobDescription.ctc === "number") {
        // Default to INR if CTC is provided but currency is missing
        payload.currency = "INR";
      }

      const response = await apiClient.post(
        `${this.APIBaseUrl}/admin/job-description/update/${id}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ JobDescriptionService ~ updateJobDescriptionV2 ~ error:",
        error
      );
    }
  }

  async generateJobDescription(value: string) {
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/admin/job-description/generate`,
        { text: value },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("🚀 ~ generateJobDescription ~ error:", error);
      throw error;
    }
  }

  async generateRequirements(
    prompt: string,
    context?: {
      initialPrompt?: string;
      step1Data?: {
        title?: string;
        position?: string;
        description?: string;
        numberOfOpenings?: number;
        ctc?: string;
        currencyCode?: string;
      };
      currentRequirements?: Array<{
        id?: number;
        topic?: string;
        description: string;
        mustMatch: boolean;
      }>;
      refinementHistory?: Array<{
        prompt: string;
        timestamp: string;
      }>;
    }
  ) {
    try {
      const payload: any = { text: prompt };

      // Add context if provided
      if (context) {
        payload.context = context;
      }

      const response = await apiClient.post(
        `${this.APIBaseUrl}/admin/job-description/generate/requirements`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("🚀 ~ generateRequirements ~ error:", error);
      throw error;
    }
  }

  async exportJobApplications(filters: any) {
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/admin/job-application/exportData`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          filters,
        }
      );
      return await response.data;
    } catch (error) {
      console.error("🚀 ~ exportJobApplications ~ error:", error);
    }
  }

  async getUploadPresignedUrl(
    fileName: string,
    fileType: string,
    jobDescriptionId: string
  ) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/job-description/bulk/upload?fileName=${fileName}&fileType=${fileType}&jobDescriptionId=${jobDescriptionId}`
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ JobDescriptionService ~ getUploadPresignedUrl ~ error:",
        error
      );
    }
  }

  async getSampleQuestions(
    requirements: Requirements[],
    round: InterviewRound
  ) {
    try {
      // Clean up the round object - only send necessary fields
      const cleanedRound = {
        _id: round._id,
        title: round.title,
        answerType: round.answerType,
        questionGenerationPrompt: round.questionGenerationPrompt,
        evaluationPrompt: round.evaluationPrompt,
        category: round.category || [],
        // Include orgId if it exists (using type assertion for optional property)
        ...((round as any).orgId && { orgId: (round as any).orgId }),
      };

      const response = await apiClient.post(
        `${this.APIBaseUrl}/admin/job-description/load/sample-questions`,
        { requirements, round: cleanedRound }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "🚀 ~ JobDescriptionService ~ getSampleQuestions ~ error:",
        error
      );
      // Re-throw the error so it can be handled by the caller
      throw error;
    }
  }

  async getAllJobDescriptionwithFilters(
    page: number,
    limit: number,
    filters: JobPostsFilters,
    filterByCreator: boolean = false
  ) {
    const { search, status, sort, order } = filters;
    try {
      let params = `page=${page}&limit=${limit}`;
      if (search) {
        params += `&search=${search}`;
      }
      if (status) {
        params += `&status=${status}`;
      }
      if (sort && order) {
        params += `&sort=${sort}&order=${order}`;
      }
      // Add filterByCreator param to enable role-based filtering
      // When true: admins see all, non-admins see only their created posts
      if (filterByCreator) {
        params += `&filterByCreator=true`;
      }

      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/job-description/find/all?${params}`
      );
      return response.data || { jobPosts: [], totalRecords: 0, totalPages: 0 };
    } catch (error) {
      console.error(
        "🚀 ~ JobDescriptionService ~ getAllJobDescriptionwithFilters ~ error:",
        error
      );
      // Return empty response structure instead of undefined
      return { jobPosts: [], totalRecords: 0, totalPages: 0 };
    }
  }

  async getAllZohoRecruitJobPosts() {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/app-integration/zoho/jobs`
      );
      return response?.data || [];
    } catch (error: any) {
      console.error(
        "🚀 ~ JobDescriptionService ~ getAllZohoRecruitJobPosts ~ error:",
        error
      );
      return [];
    }
  }
}

const jobDescriptionService = new JobDescriptionService();
export default jobDescriptionService;
